import { useEffect } from 'react';
import { useMatch, useNavigate } from 'react-router-dom';

import { observer } from 'mobx-react-lite';

import { IconFile } from '@components/icons/file';
import { IconList } from '@components/icons/list';
import { IconNotes } from '@components/icons/notes';
import { Tabs, TabItem } from '@components/tabs';

import { PageContent } from 'components/Page';
import { useAuthenticatedUser } from 'features/authentication';
import { useStore } from 'models/Provider';
import { IJob, IJobApplicationFollower, IUser } from 'models/types';
import { IAuthenticatedUser } from 'types/authentication';

import { Details } from './Details';
import { Files } from './Files';
import { Notes } from './Notes';

interface Props {
  job: IJob;
}

function hasAccessToNotesAndFiles(job: IJob, me: IAuthenticatedUser) {
  if (job.followers.some((follower: IUser) => follower.id === me.id)) {
    return true;
  } else if (
    job.appFollowers.find((appFollower: IJobApplicationFollower) => appFollower.id === me.id) &&
    me.addToHiredApplications
  ) {
    return true;
  } else if (me.role === 'superadmin') {
    return true;
  }
  return false;
}

export const Info = observer((props: Props) => {
  const { t } = useStore();
  const me = useAuthenticatedUser();
  const navigate = useNavigate();
  const { job } = props;
  const section = useMatch('/jobs/:jobID/info/:section')?.params.section;

  useEffect(() => {
    job.loadFromDb();
  }, [job]);

  let items: Array<TabItem> = [
    {
      icon: <IconList />,
      label: t('job.info.tabs.details'),
      content: <Details job={job} />,
      onClick: () => navigate(`/jobs/${job.id}/info/details`),
    },
  ];
  if (hasAccessToNotesAndFiles(job, me)) {
    items.push(
      {
        icon: <IconNotes />,
        count: job.notes.length,
        label: t('job.info.tabs.notes'),
        active: job.reads && !job.reads.notes,
        content: <Notes job={job} />,
        onClick: () => navigate(`/jobs/${job.id}/info/notes`),
      },
      {
        icon: <IconFile />,
        count: job.attachments.length,
        active: job.reads && !job.reads.files,
        label: t('job.info.tabs.files'),
        content: <Files job={job} />,
        onClick: () => navigate(`/jobs/${job.id}/info/files`),
      }
    );
  }
  return (
    <PageContent>
      <Tabs key={items.length} items={items} selectedIndex={indexMapper(section)} />
    </PageContent>
  );
});

function indexMapper(section?: string): number {
  switch (section) {
    case 'notes':
      return 1;
    case 'files':
      return 2;
    case 'details':
    default:
      return 0;
  }
}
